import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gift, Leaf, Award, Sparkles, Coins } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const TYPE_META = {
  gift_card: { icon: <Gift className="h-4 w-4" />, tone: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  impact: { icon: <Leaf className="h-4 w-4" />, tone: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  coupon: { icon: <Sparkles className="h-4 w-4" />, tone: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300" },
  membership: { icon: <Award className="h-4 w-4" />, tone: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
};

const RewardsPage = () => {
  const { user, loading } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/rewards").then((r) => setRewards(r.data));
    if (user) api.get("/dashboard").then((r) => setStats(r.data.stats));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/rewards" replace />;

  const redeem = async (r) => {
    try {
      const { data } = await api.post("/rewards/redeem", { reward_id: r.id });
      setStats(data.stats);
      toast.success(`Redeemed: ${r.title}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to redeem");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Redeem Rewards</h1>
          <p className="text-muted-foreground mt-2">Turn your Green Points into real-world impact and rewards.</p>
        </div>
        <div className="card-premium p-5 flex items-center gap-4">
          <div className="grid place-items-center h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
            <Coins className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Available balance</div>
            <div className="mono text-2xl font-bold" data-testid="points-balance">{stats?.available_points ?? "…"} pts</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rewards-grid">
        {rewards.map((r) => {
          const meta = TYPE_META[r.type] || TYPE_META.gift_card;
          const canAfford = (stats?.available_points ?? 0) >= r.cost;
          return (
            <div key={r.id} className="card-premium overflow-hidden flex flex-col" data-testid={`reward-${r.id}`}>
              <div className="aspect-[16/9] overflow-hidden bg-secondary">
                <img src={r.image} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 self-start ${meta.tone}`}>
                  {meta.icon}{r.type.replace("_", " ")}
                </span>
                <h3 className="font-semibold text-lg mt-3 leading-snug">{r.title}</h3>
                <div className="mt-auto pt-4 flex items-center justify-between">
                  <span className="mono font-bold text-emerald-600">{r.cost} pts</span>
                  <button
                    onClick={() => redeem(r)}
                    disabled={!canAfford}
                    className="btn-pill bg-primary text-primary-foreground text-sm py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`redeem-${r.id}`}
                  >
                    {canAfford ? "Redeem" : "Not enough pts"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RewardsPage;
